import React, { useState, useRef, useEffect } from 'react';
import { FileText, Scale, MessageSquare, Globe, Handshake, ChevronDown } from 'lucide-react';
import { LLMMode, LLM_MODE_CONFIG } from '../../../../services/legalAIService';

interface LLMModeSelectorProps {
  mode: LLMMode;
  onChange: (mode: LLMMode) => void;
}

const MODE_ICONS: Record<LLMMode, React.ReactNode> = {
  'standard': <FileText className="w-4 h-4" />,
  'legal-deep-dive': <Scale className="w-4 h-4" />,
  'simplified': <MessageSquare className="w-4 h-4" />,
  'jurisdiction': <Globe className="w-4 h-4" />,
  'negotiation': <Handshake className="w-4 h-4" />
};

const MODE_STYLES: Record<LLMMode, { bg: string; border: string; text: string; hover: string }> = {
  'standard': {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-300',
    hover: 'hover:border-blue-300 dark:hover:border-blue-700'
  },
  'legal-deep-dive': {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-200 dark:border-purple-800',
    text: 'text-purple-700 dark:text-purple-300',
    hover: 'hover:border-purple-300 dark:hover:border-purple-700'
  },
  'simplified': {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-700 dark:text-green-300',
    hover: 'hover:border-green-300 dark:hover:border-green-700'
  },
  'jurisdiction': {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    border: 'border-orange-200 dark:border-orange-800',
    text: 'text-orange-700 dark:text-orange-300',
    hover: 'hover:border-orange-300 dark:hover:border-orange-700'
  },
  'negotiation': {
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-700 dark:text-red-300',
    hover: 'hover:border-red-300 dark:hover:border-red-700'
  }
};

export const LLMModeSelector: React.FC<LLMModeSelectorProps> = ({ mode, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentConfig = LLM_MODE_CONFIG[mode];
  const styles = MODE_STYLES[mode];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border-2 transition-all ${styles.bg} ${styles.border} ${styles.hover}`}
      >
        <span className={styles.text}>{MODE_ICONS[mode]}</span>
        <div className="text-left">
          <p className={`font-medium ${styles.text}`}>{currentConfig.label}</p>
          <p className={`text-xs opacity-75 ${styles.text}`}>{currentConfig.description}</p>
        </div>
        <ChevronDown className={`w-4 h-4 ${styles.text} transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-dark-surface rounded-lg shadow-lg border border-gray-200 dark:border-dark-border z-20 overflow-hidden">
          {(Object.entries(LLM_MODE_CONFIG) as [LLMMode, typeof LLM_MODE_CONFIG[LLMMode]][]).map(([key, config]) => {
            const itemStyles = MODE_STYLES[key];
            const isSelected = key === mode;

            return (
              <button
                key={key}
                onClick={() => {
                  onChange(key);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  isSelected ? itemStyles.bg : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <span className={itemStyles.text}>{MODE_ICONS[key]}</span>
                <div className="flex-1">
                  <p className={`font-medium ${isSelected ? itemStyles.text : 'text-gray-900 dark:text-white'}`}>
                    {config.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{config.description}</p>
                </div>
                {isSelected && (
                  <div className={`w-2 h-2 rounded-full ${itemStyles.bg} ${itemStyles.border} border-2`} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LLMModeSelector;
